import React from 'react';
import styled from 'styled-components';

import { Grid } from '@/ui/Grid/atoms';
import { JourneyParticipant, JourneyParticipantProps } from '@/features/journey/admin/atoms/JourneyParticipant';
import { makeNamespacedComponent } from '@/ui/utils';
import { Hero } from '@/administration/pages/Journey/common/molecules/JourneyHero';
import { JourneyViewListCard } from '@/features/journey/admin/molecules/JourneyViewListCard';
import { JourneyPageWrapper } from '@/features/journey/admin/pages/JourneyPageWrapper';
import { JourneyAdminButtonGroup } from '@/features/journey/admin/atoms/JourneyAdminButtonGroup';
import { OrderedLearningCardProps } from '@/administration/pages/Journey/common/atoms/LearningCard/variants/OrderedLearningCard';
import { JourneyViewDescription } from '@/features/journey/admin/atoms/JourneyViewDescription';
import { ParticipationV3 } from '@/store/v2/catalog-v2/model/participation';
import { LearningJourney } from '@/store/v2/journeys/journey';
import { JourneyViewDiscussionsBox } from '@/features/journey/admin/atoms/JourneyViewDiscussionsBox';
import { useCurrentUserAccess } from '@/store/currentUser/userCurrentUserAccess';
import { LearnersOrderedLearningCard } from '@/administration/pages/Journey/common/atoms/LearningCard/variants/LearnersOrderedLearningCard/LearnersOrderedLearningCard';
import { JourneyViewOverviewBox } from '@/features/journey/admin/atoms/JourneyViewOverviewBox';
import { RLearningContentProvider, RLearningJourneyCompletionStatus, RLearningType } from '@/store/v2/remote.gen';
import { JourneyViewSectionListCard } from '@/features/journey/admin/molecules/JourneyViewSectionListCard';
import { JourneyViewProgressBox } from '@/features/journey/admin/atoms/JourneyViewProgressBox';
import { LearningCardProps } from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCard';
import { JourneyViewUpcomingEventBox } from '@/features/journey/admin/atoms/JourneyViewUpcomingEventBox';
import { JourneyViewCertificateBanner } from '@/features/journey/admin/atoms/JourneyViewCertificateBanner';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Separator = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  margin: 32px 0;
`;

// Neutralises the 24px margin-top on JourneyViewDescription's Title so the banner
// sits at the Figma design specified distance from the description heading.
const CertificateBannerSlotStyled = styled.div`
  margin-bottom: -24px;
`;

type LearningItemProps = OrderedLearningCardProps & {
  objectId?: string;
};

type JourneyViewLayoutProps = {
  journey: LearningJourney;
  /** Description/business goal resolved from the V2 rich text fields into plain strings. */
  description?: string;
  businessGoal?: string;

  learnersPage: JourneyParticipantProps[];
  learnersTotal: number;
  onLearnersViewAll: () => void;

  learningItems: LearningItemProps[];
  learningItemsTotal: number;

  section?: {
    id: number;
    title: string;
    description: string;
    totalEffort?: number;
    childrenCount?: number;
  };
  sectionLoading?: boolean;
  sectionLearningItems: LearningItemProps[];

  onExpandDescription: () => void;

  isSavedFavourite: boolean;
  onSaveFavourite: () => void;

  journeyParticipation: ParticipationV3 | undefined;
  journeyParticipationLoading: boolean;

  onJoinJourneyLoading: boolean;
  onJoinJourney: () => void;
  onPreJoinItemClick: () => void;

  upcomingEventItem?: LearningCardProps;
  upcomingEventItemLoading?: boolean;

  nextLearningItem?: LearningCardProps;
  nextLearningItemLoading?: boolean;
  allUpcomingItemsLocked?: boolean;
  allUpcomingItemsUnlockAt?: Date | string;

  isInPreviewModal?: boolean;

  showCertificateBanner?: boolean;
  learnerFirstName?: string;
  onSeeCertificate?: () => void;
};

const JourneyViewLayout = ({
  journey,
  description,
  businessGoal,
  learnersPage,
  learnersTotal,
  onLearnersViewAll,
  section,
  sectionLoading,
  sectionLearningItems,
  learningItems,
  onExpandDescription,
  isSavedFavourite,
  onSaveFavourite,
  journeyParticipation,
  journeyParticipationLoading,
  onJoinJourney,
  onPreJoinItemClick,
  isInPreviewModal,
  upcomingEventItem,
  upcomingEventItemLoading,
  nextLearningItem,
  nextLearningItemLoading,
  allUpcomingItemsLocked,
  allUpcomingItemsUnlockAt,
  showCertificateBanner,
  learnerFirstName,
  onSeeCertificate,
}: JourneyViewLayoutProps): JSX.Element => {
  const { isAdmin, loading } = useCurrentUserAccess();
  const displayAdminButtonGroup = !loading && isAdmin && !isInPreviewModal;

  const createOnClickCallback = (props: LearningItemProps) => {
    if (!journeyParticipation && props.type !== RLearningType.Section) return onPreJoinItemClick;

    return undefined;
  };

  const renderDescription = () => {
    if (!description && !businessGoal) return null;

    return (
      <JourneyViewDescription
        description={description || ''}
        businessGoal={businessGoal || ''}
        spaceId={journey.spaceId || 0}
        onShowMore={onExpandDescription}
      />
    );
  };

  const renderLearningItems = () => (
    <JourneyViewListCard
      emptyProps={{
        title: 'No learning items yet!',
      }}
    >
      {learningItems.map((props) => (
        <LearnersOrderedLearningCard {...props} key={props.orderIndex} onClick={createOnClickCallback(props)} isInPreviewModal />
      ))}
    </JourneyViewListCard>
  );

  const renderSectionLearningItems = () => {
    if (!section) return null;

    return (
      <JourneyViewSectionListCard section={section} journey={journey}>
        {sectionLearningItems.map((props) => (
          <LearnersOrderedLearningCard
            {...props}
            key={props.orderIndex}
            onClick={createOnClickCallback(props)}
            isInPreviewModal
          />
        ))}
      </JourneyViewSectionListCard>
    );
  };

  const renderParticipants = () => (
    <JourneyViewListCard
      itemsCount={learnersTotal}
      title="Learners"
      onViewClick={onLearnersViewAll}
      emptyProps={{
        title: 'Be the first to join!',
      }}
      styleBordered
    >
      {learnersPage.slice(0, Math.min(learnersPage.length, 5)).map((props) => (
        <JourneyParticipant {...props} key={props.email} />
      ))}
    </JourneyViewListCard>
  );

  const renderDiscussionsBox = () => {
    if (journeyParticipationLoading) return <JourneyViewDiscussionsBox.LoadingState />;

    if (!journey.discussionEnabled) return null;

    return <JourneyViewDiscussionsBox journeyId={journey?.id} journeyTitle={journey?.title} />;
  };

  const renderUpcomingEventBox = () => {
    if (upcomingEventItemLoading) return <JourneyViewUpcomingEventBox.LoadingState />;

    if (!upcomingEventItem) return null;

    return (
      <JourneyViewUpcomingEventBox
        item={upcomingEventItem}
        journeyId={journey.id}
        onPreJoinItemClick={!journeyParticipation ? onPreJoinItemClick : undefined}
      />
    );
  };

  const renderOverview = () => {
    if (journeyParticipationLoading) return <JourneyViewOverviewBox.LoadingState />;

    const requiredItems = learningItems.filter((item) => !item.isOptional);

    const requiredItemsCount = requiredItems.length;
    if (!journeyParticipation) {
      return (
        <JourneyViewOverviewBox
          onJoin={onJoinJourney}
          requiredItemsCount={requiredItemsCount}
          totalEffort={
            journey.completionCriteria === RLearningJourneyCompletionStatus.InvestTime
              ? journey.completionInvestTimeSec || 0
              : undefined
          }
        />
      );
    }

    return (
      <JourneyViewProgressBox
        requiredItemsCount={requiredItemsCount}
        progress={journeyParticipation}
        journey={journey}
        nextLearningItem={nextLearningItem}
        nextLearningItemLoading={nextLearningItemLoading}
        allUpcomingItemsLocked={allUpcomingItemsLocked}
        allUpcomingItemsUnlockAt={allUpcomingItemsUnlockAt}
      />
    );
  };

  return (
    <JourneyPageWrapper>
      {displayAdminButtonGroup && <JourneyAdminButtonGroup journeyId={journey.id} />}
      <Hero
        isSaved={isSavedFavourite}
        onSave={onSaveFavourite}
        title={journey.title}
        imageUrl={journey.bannerURL || undefined}
        provider={RLearningContentProvider.Collegial}
      />
      {showCertificateBanner && onSeeCertificate && (
        <CertificateBannerSlotStyled>
          <JourneyViewCertificateBanner firstName={learnerFirstName} onSeeCertificate={onSeeCertificate} />
        </CertificateBannerSlotStyled>
      )}
      <Grid rowGap="medium">
        <Grid.Item mobile={12} mobileLandscape={12} tablet={12} desktop={8} desktopLarge={8} widescreen={8} fullHD={8}>
          {renderDescription()}
          {!section && !sectionLoading && renderLearningItems()}
          {sectionLoading && <JourneyViewListCard.LoadingState />}
          {section && !sectionLoading && renderSectionLearningItems()}
        </Grid.Item>
        <Grid.Item mobile={12} mobileLandscape={12} tablet={12} desktop={4} desktopLarge={4} widescreen={4} fullHD={4}>
          <List>
            {renderOverview()}
            {renderUpcomingEventBox()}
            {renderDiscussionsBox()}
            {renderParticipants()}
          </List>
        </Grid.Item>
      </Grid>
    </JourneyPageWrapper>
  );
};

const LoadingState = () => (
  <JourneyPageWrapper>
    <Hero.LoadingState />
    <Grid rowGap="medium">
      <Grid.Item mobile={12} mobileLandscape={12} tablet={12} desktop={8} desktopLarge={8} widescreen={8} fullHD={8}>
        <JourneyViewDescription.LoadingState />
        <Separator />
        <JourneyViewListCard.LoadingState />
      </Grid.Item>
      <Grid.Item mobile={12} mobileLandscape={12} tablet={12} desktop={4} desktopLarge={4} widescreen={4} fullHD={4}>
        <List>
          <JourneyViewDiscussionsBox.LoadingState />
          <JourneyViewListCard.LoadingState />
        </List>
      </Grid.Item>
    </Grid>
  </JourneyPageWrapper>
);

const _JourneyViewLayout = makeNamespacedComponent(JourneyViewLayout, {
  LoadingState,
});

export { _JourneyViewLayout as JourneyViewLayout };
